import AppModal from "components/Modal";
import React, { useState } from 'react';
import ReactDOM from "react-dom";
import { useAuth } from '../../../helpers/authContext';
import { Button } from "reactstrap";
import { useToast } from "components/ui/use-toast";
import useModal from "helpers/useModal";
import LoginModal from "features/user/userModals/LoginModal";
import { isLoggedWithKeychain } from "services/dsteem-service";
import { keychainProposalVote } from "helpers/keychain";
import { proposalWifVote } from "helpers/steem";

type Props = {
    isShowing: boolean;
    hide: () => void;
    proposalId: number | string;
    onSuccess?: () => void;
};

export const VotingModal = ({ isShowing, hide, proposalId, onSuccess = undefined }: Props) => {
    const { user, autologin, proposals, updateProposals } = useAuth();
    const [requiredActive, setRequiredActive] = useState<any>(true);
    const [isVoting, setIsVoting] = useState(false);
    const keychainLogin = isLoggedWithKeychain(autologin);
    const { isOpenLoginModal, toggleLoginModal } = useModal();
    const { toast } = useToast();

    // Check if the user has already voted for this proposal
    const isVoted = proposals?.length > 0 && proposals.includes(Number(proposalId));

    const handleVote = async (wif = "") => {
        if (!proposalId) return;
        
        // We always try to 'support' (voteStatus = true) when loading from an external link,
        // unless they are already supporting it, in which case we might be removing support,
        // but for an auto-load link, it's usually to add support. We will use voteStatus = true.
        const voteStatus = true;

        if (isVoted) {
            toast({
                title: "Already Supported",
                description: "You are already supporting this proposal.",
                variant: "wild_green"
            });
            hide();
            return;
        }

        setIsVoting(true);

        try {
            let success = false;
            let errorMessage = "";

            if (keychainLogin) {
                const res = await keychainProposalVote(voteStatus, user, Number(proposalId));
                success = res === true;
                if (!success) errorMessage = "Failed to process vote with Keychain.";
            } else {
                const res: any = await proposalWifVote(voteStatus, autologin, Number(proposalId), wif);
                success = res.success;
                if (!success) {
                    if (res?.result?.message?.toString().includes("Missing Active Authority")) {
                        setRequiredActive(true);
                        toggleLoginModal();
                        setIsVoting(false);
                        return;
                    } else {
                        errorMessage = res.result || "Failed to process vote.";
                    }
                }
            }

            if (!success) {
                toast({
                    title: "Failed voting for proposal.",
                    description: errorMessage,
                    variant: "rose_red"
                });
            } else {
                toast({
                    title: "Proposal supported successfully.",
                    description: "Thank you for supporting this proposal.",
                    variant: "wild_green"
                });
                
                // Update local state optimistic
                if (updateProposals && !isVoted) {
                    updateProposals([...proposals, Number(proposalId)]);
                }

                if (onSuccess) {
                    onSuccess();
                }
                hide();
            }
        } catch (error: any) {
            console.error("Voting error:", error);
            toast({ title: "Error", description: "An unexpected error occurred.", variant: "rose_red" });
        } finally {
            setIsVoting(false);
        }
    }

    return isShowing ? ReactDOM.createPortal(
        <React.Fragment>
            <AppModal isShowing={isShowing} hide={() => !isVoting && hide()} size="md" header="Confirm Proposal Support">
                <div className="p-3 text-scheme">
                    <p className="text-muted">You are about to support a Steem Proposal. Please review the details:</p>
                    <div className="mb-4 p-3 border rounded bg-speem-confirm">
                        <div className="mb-2">
                            <i className="fas fa-hashtag mr-2 text-speem" /> <strong>Proposal ID:</strong> <span className="ml-2">#{proposalId}</span>
                        </div>
                        <div className="mb-0">
                            <i className="fas fa-vote-yea mr-2 text-speem" /> <strong>Action:</strong> <span className="ml-2 text-success">Support (Vote)</span>
                        </div>
                    </div>
                    
                    {isVoted && (
                        <div className="alert alert-success mt-2 mb-2">
                            <i className="fas fa-check-circle mr-2" /> You are already supporting this proposal.
                        </div>
                    )}

                    <div className="d-flex gap-3 mt-4">
                        <Button color="secondary" onClick={() => hide()} disabled={isVoting} className="w-100 mr-2 btn-fancy">
                            Cancel
                        </Button>
                        <Button color="default" onClick={() => handleVote()} disabled={isVoting || isVoted} className="w-100 btn-fancy position-relative">
                            {isVoting ? (
                                <span className="spinner dark m-0" />
                            ) : keychainLogin ? (
                                <>
                                Confirm with
                                <img className="icon icon-sm ml-1" src="/assets/img/keychain.svg" />
                                </>
                            ) : (
                                "Confirm"
                            )}
                        </Button>
                    </div>
                </div>
            </AppModal>
            <LoginModal
                isShowing={isOpenLoginModal}
                hide={toggleLoginModal}
                requiredActive={requiredActive}
                onSuccess={({ wif }) => {
                    if (wif) {
                        handleVote(wif);
                    }
                }}
            />
        </React.Fragment>, document.body
    ) : null;
};

export default VotingModal;